import React, { useEffect, useState } from 'react';
import tofLogo from '../assets/tof-logo.png';
import { getMyManagedTeams, validateTeamAccessCode } from '../supabase';
import { grantTeamAccess } from '../utils/access';
import {
  PageShell,
  PrimaryButton,
  SecondaryButton,
} from '../ui/AppShell';
import { useCopy } from '../i18n/LanguageContext';
import { IS_NATIVE } from '../config/platform';
import { BOOKING_URL } from '../config/contact';

export default function Home({ setPage }) {
  const { home: t } = useCopy();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);
  const [showLock, setShowLock] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [lockError, setLockError] = useState('');
  const [checkingCode, setCheckingCode] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Managers horen niet op de marketing/home — stuur ze direct door
  // naar /team (manager-mode). Vangt twee scenario's op:
  //   1. AuthCallback heeft (om welke reden dan ook) niet correct
  //      gerouteerd na login.
  //   2. Een manager klikt later in de nav op "Home" — ook dan terug.
  // In app-modus bestaat de teamomgeving niet en is er geen sessie — dus
  // ook geen managercheck (en geen netwerkcall).
  useEffect(() => {
    if (IS_NATIVE) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const managed = await getMyManagedTeams();
        if (!cancelled && managed.length > 0 && typeof setPage === 'function') {
          setPage('team');
        }
      } catch (_e) {
        // niet-kritiek
      }
    })();
    return () => { cancelled = true; };
  }, [setPage]);

  function openTeamLock() {
    setShowLock(true);
    setAccessCode('');
    setLockError('');
    setCheckingCode(false);
  }

  function closeTeamLock() {
    setShowLock(false);
    setAccessCode('');
    setLockError('');
    setCheckingCode(false);
  }

  async function handleUnlock() {
    const code = accessCode.trim();

    if (!code) {
      setLockError(t.lock.errorEmpty);
      return;
    }

    setCheckingCode(true);
    setLockError('');

    // Valideer de code tegen team_access_codes (de bron van toegangscodes),
    // niet tegen responses — zo hoeven bezoekers geen leesrecht op responses
    // te hebben (zie beveiligings-lockdown 006).
    let access = null;
    try {
      access = await validateTeamAccessCode(code);
    } catch (err) {
      console.error(err);
      setLockError(t.lock.errorCheck);
      setCheckingCode(false);
      return;
    }

    if (!access) {
      setLockError(t.lock.errorUnknown);
      setCheckingCode(false);
      return;
    }

    grantTeamAccess(code);
    closeTeamLock();
    // De page-key is 'team' (zie i18n/routes.js). Met 'teamintro' bestaat er
    // geen pad en valt pagePath terug op de landingspagina.
    setPage('team');
  }

  return (
    <PageShell padding={isMobile ? '20px 16px 28px' : '24px 20px 36px'}>
      <div
        style={{
          animation: 'tofFadeIn 0.5s ease',
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: isMobile ? 24 : 36,
          width: '100%',
        }}
      >
        <div
          style={{
            background: '#ffffff',
            borderRadius: 18,
            borderLeft: '4px solid var(--tof-accent-rose)',
            boxShadow: '0 10px 26px rgba(70, 45, 35, 0.05)',
            padding: isMobile ? '22px 20px 22px 22px' : '32px 36px 32px 36px',
            display: 'grid',
            gap: isMobile ? 18 : 22,
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 300px',
              gap: isMobile ? 14 : 36,
              alignItems: 'start',
            }}
          >
            <div>
              <div
                style={{
                  color: 'var(--tof-accent-rose)',
                  letterSpacing: 2,
                  fontSize: 12,
                  marginBottom: 12,
                  textTransform: 'uppercase',
                  fontWeight: 600,
                }}
              >
                {t.eyebrow}
              </div>

              <h1
                style={{
                  fontSize: isMobile ? 34 : 44,
                  lineHeight: 1.05,
                  fontFamily: 'Playfair Display',
                  fontWeight: 500,
                  margin: 0,
                  color: '#1f1b18',
                }}
              >
                {t.heroTitle}{' '}
                <span
                  style={{
                    // `font-family: inherit` is hier nodig: index.css zet
                    // alle span's op de body-font, en die vlakke regel wint
                    // van overerving. Zonder dit werd de cursieve kopregel
                    // een kunstmatig schuingezette Inter i.p.v. Playfair.
                    fontFamily: 'inherit',
                    color: 'var(--tof-accent-rose)',
                    fontStyle: 'italic',
                  }}
                >
                  {t.heroTitleAccent}
                </span>
              </h1>
            </div>

            <p
              style={{
                margin: 0,
                lineHeight: 1.6,
                color: '#444',
                fontSize: isMobile ? 15 : 16,
              }}
            >
              {t.heroLead}
            </p>
          </div>

          {/* Twee routes: met een team naar de teamomgeving, zonder team naar
              de app. De test zelf zit niet meer op deze site (behalve voor
              wie via een uitnodigingslink komt — die slaat Home over). */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <PrimaryButton onClick={openTeamLock}>
              {t.ctaTeamEnvironment}
            </PrimaryButton>

            <SecondaryButton onClick={() => setPage('quiz')}>
              {t.ctaApp}
            </SecondaryButton>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              isMobile || IS_NATIVE ? '1fr' : 'repeat(2, minmax(0, 1fr))',
            gap: isMobile ? 14 : 24,
            alignItems: 'stretch',
          }}
        >
          {!IS_NATIVE && (
            <GreenTeamCard copy={t.teamCard} onClick={() => setPage('team')} />
          )}

          <OfferCard
            eyebrow={t.offerCard.eyebrow}
            title={t.offerCard.title}
            text={t.offerCard.text}
            accent="var(--tof-accent-sage)"
            eyebrowColor="var(--tof-accent-sage-text)"
            onClick={() => setPage('quiz')}
            clickable
          />
        </div>

        <div
          style={{
            marginTop: isMobile ? 4 : 10,
            paddingTop: isMobile ? 18 : 24,
            borderTop: '1px solid var(--tof-border)',
            display: 'grid',
            gap: 14,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <img
              src={tofLogo}
              alt={t.footer.logoAlt}
              style={{
                width: 34,
                height: 34,
                objectFit: 'contain',
                flexShrink: 0,
              }}
            />

            <div
              style={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 300,
                fontSize: '1.125rem',
                letterSpacing: '-0.025em',
                lineHeight: 1.25,
                color: '#1F1F1F',
                display: 'inline-flex',
                alignItems: 'baseline',
                gap: 0,
              }}
            >
              <span
                style={{
                  fontFamily: '"Playfair Display", serif',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  color: '#1F1F1F',
                  marginRight: '0.15em',
                }}
              >
                The Office
              </span>

              <span
                style={{
                  fontFamily: '"Playfair Display", serif',
                  fontWeight: 300,
                  fontStyle: 'italic',
                  color: '#B05252',
                }}
              >
                Factory
              </span>
            </div>
          </div>

          <p
            style={{
              margin: 0,
              maxWidth: 640,
              fontSize: 14,
              lineHeight: 1.7,
              color: 'var(--tof-text-soft)',
            }}
          >
            {t.footer.body}
          </p>

          {/* Links naar buiten horen niet in de app — die stuurt je de
              browser uit en vormt een afkeuringsgrond in de stores. */}
          {!IS_NATIVE && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() =>
                  window.open('https://www.tof.services', '_blank', 'noopener,noreferrer')
                }
                style={footerLinkRose}
              >
                {t.footer.website}
              </button>

              <button
                type="button"
                onClick={() =>
                  window.open(
                    BOOKING_URL,
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
                style={footerLinkDark}
              >
                {t.footer.contact}
              </button>
            </div>
          )}

          <p
            style={{
              margin: 0,
              fontSize: 12,
              lineHeight: 1.6,
              color: 'var(--tof-text-muted)',
            }}
          >
            {IS_NATIVE ? t.footer.privacyNative : t.footer.privacy}
          </p>
        </div>
      </div>
      {showLock && (
        <div
          onClick={closeTeamLock}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 420,
              background: '#fff',
              borderRadius: 24,
              border: '1px solid #E5D9CD',
              boxShadow: '0 24px 70px rgba(40, 28, 22, 0.18)',
              padding: isMobile ? '20px 18px' : '24px 24px',
              display: 'grid',
              gap: 14,
            }}
          >
            <div
              style={{
                fontSize: 12,
                textTransform: 'uppercase',
                letterSpacing: 1.8,
                color: 'var(--tof-text-muted)',
                fontWeight: 700,
              }}
            >
              {t.lock.eyebrow}
            </div>

            <div
              style={{
                fontFamily: 'var(--tof-font-heading)',
                fontSize: isMobile ? 28 : 32,
                lineHeight: 1.05,
                color: 'var(--tof-text)',
              }}
            >
              {t.lock.title}
            </div>

            <p
              style={{
                margin: 0,
                color: 'var(--tof-text-soft)',
                fontSize: 14,
                lineHeight: 1.65,
              }}
            >
              {t.lock.body}
            </p>

            <input
              type="text"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              placeholder={t.lock.placeholder}
              autoFocus
              style={{
                width: '100%',
                padding: '14px 16px',
                borderRadius: 14,
                border: '1px solid #E2D8CC',
                fontSize: 15,
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !checkingCode) {
                  handleUnlock();
                }
              }}
            />

            {lockError ? (
              <div
                style={{
                  fontSize: 13,
                  color: '#B05252',
                  lineHeight: 1.5,
                }}
              >
                {lockError}
              </div>
            ) : null}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <PrimaryButton
                onClick={handleUnlock}
                style={{ flex: 1 }}
                disabled={checkingCode}
              >
                {checkingCode ? t.lock.checking : t.lock.submit}
              </PrimaryButton>

              <SecondaryButton onClick={closeTeamLock}>
                {t.lock.close}
              </SecondaryButton>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}

function OfferCard({
  eyebrow,
  title,
  text,
  accent = 'var(--tof-text)',
  eyebrowColor,
  onClick,
  clickable = false,
  subtle = false,
}) {
  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={
        clickable
          ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') onClick?.();
          }
          : undefined
      }
      style={{
        background: subtle ? '#FBF8F4' : 'var(--tof-surface)',
        borderRadius: 16,
        padding: 22,
        border: '1px solid var(--tof-border)',
        // Ná `border`, anders overschrijft die shorthand de accentrand.
        borderTop: `4px solid ${accent}`,
        display: 'grid',
        gridTemplateRows: 'auto auto 1fr',
        gap: 10,
        boxShadow: subtle ? 'none' : 'var(--tof-shadow)',
        minHeight: 210,
        alignContent: 'start',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        cursor: clickable ? 'pointer' : 'default',
        opacity: subtle ? 0.96 : 1,
      }}
      onMouseEnter={(e) => {
        if (!clickable) return;
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 16px 32px rgba(0,0,0,0.06)';
      }}
      onMouseLeave={(e) => {
        if (!clickable) return;
        e.currentTarget.style.transform = 'translateY(0px)';
        e.currentTarget.style.boxShadow = subtle ? 'none' : 'var(--tof-shadow)';
      }}
    >
      <div
        style={{
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: 1.6,
          color: eyebrowColor || accent,
          fontWeight: 700,
        }}
      >
        {eyebrow}
      </div>

      <h3
        style={{
          fontFamily: 'var(--tof-font-heading)',
          margin: 0,
          fontSize: 22,
          lineHeight: 1.12,
          color: 'var(--tof-text)',
        }}
      >
        {title}
      </h3>

      <p
        style={{
          margin: 0,
          color: 'var(--tof-text-soft)',
          fontSize: 14,
          lineHeight: 1.7,
          maxWidth: 560,
        }}
      >
        {text}
      </p>
    </div>
  );
}

function GreenTeamCard({ copy, onClick }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick?.();
      }}
      style={{
        // Donkerder dan sage (besluit Judith 25 okt): de lichte tekst haalt zo
        // overal minstens 4,91 : 1.
        background: 'linear-gradient(135deg, #56705C 0%, #4A6250 100%)',
        borderRadius: 16,
        padding: 22,
        border: '1px solid rgba(255,255,255,0.08)',
        // Ná `border`, anders overschrijft die shorthand de bovenrand. Rand =
        // eindkleur van het verloop, zoals hij altijd bedoeld was.
        borderTop: '4px solid #4A6250',
        boxShadow: 'var(--tof-shadow)',
        display: 'grid',
        gap: 12,
        minHeight: 210,
        alignContent: 'start',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 16px 32px rgba(0,0,0,0.06)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0px)';
        e.currentTarget.style.boxShadow = 'var(--tof-shadow)';
      }}
    >
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: 1.6,
          color: '#F7F3EE',
          fontWeight: 700,
        }}
      >
        {copy.eyebrow}
      </div>

      <h2
        style={{
          position: 'relative',
          zIndex: 1,
          margin: 0,
          fontFamily: 'var(--tof-font-heading)',
          fontSize: 24,
          lineHeight: 1.08,
          color: '#F7F3EE',
          maxWidth: 360,
        }}
      >
        {copy.titleLine1}
        <br />
        {copy.titleLine2}
      </h2>

      <p
        style={{
          position: 'relative',
          zIndex: 1,
          margin: 0,
          fontSize: 15,
          lineHeight: 1.7,
          color: '#F7F3EE',
          maxWidth: 470,
        }}
      >
        {copy.text}
      </p>
    </div>
  );
}

const footerLinkRose = {
  background: 'transparent',
  border: 'none',
  color: 'var(--tof-accent-rose)',
  cursor: 'pointer',
  fontWeight: 500,
  padding: 0,
  fontSize: 14,
};

const footerLinkDark = {
  background: 'transparent',
  border: 'none',
  color: 'var(--tof-text)',
  cursor: 'pointer',
  padding: 0,
  fontSize: 14,
};